import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { clearRouteDeviceData } from "@cafegestor/client/offline";
import { setActiveCompanyId } from "@/lib/company-store";
import { authConfigured, supabase } from "@/lib/supabase";

/** Marcado em main.tsx quando o link do e-mail é de convite ou recuperação de senha. */
export const NEEDS_PASSWORD_KEY = "cafegestor.definir-senha";

type AuthState = {
  configured: boolean;
  status: "loading" | "signed_out" | "signed_in";
  session: Session | null;
  needsPassword: boolean;
  clearNeedsPassword: () => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

function readNeedsPassword() {
  try {
    return sessionStorage.getItem(NEEDS_PASSWORD_KEY) === "1";
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>(supabase ? "loading" : "signed_out");
  const [needsPassword, setNeedsPassword] = useState(readNeedsPassword);

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setStatus(data.session ? "signed_in" : "signed_out");
    });
    const { data } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setStatus(s ? "signed_in" : "signed_out");
      if (event === "PASSWORD_RECOVERY") setNeedsPassword(true);
      if (event === "SIGNED_OUT") {
        setActiveCompanyId(null);
        queryClient.clear();
        // listas da rota guardadas no aparelho (clientes...) não ficam para o próximo usuário
        clearRouteDeviceData();
      }
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      configured: authConfigured,
      status,
      session,
      needsPassword,
      clearNeedsPassword: () => {
        try {
          sessionStorage.removeItem(NEEDS_PASSWORD_KEY);
        } catch {
          /* sem armazenamento */
        }
        setNeedsPassword(false);
      },
      signOut: async () => {
        await supabase?.auth.signOut();
      },
    }),
    [status, session, needsPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth fora do AuthProvider");
  return ctx;
}
